import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";

const varieties = [
  { name: "Arabica", tag: "Smooth Flavor", desc: "Hand-picked at 1,100m — jasmine, honey, and a silky finish.", slug: "arabica" },
  { name: "Robusta", tag: "Bold Strength", desc: "Rich crema, deep chocolate, unapologetic strength.", slug: "robusta" },
  { name: "Blends", tag: "Crafted Harmony", desc: "Balanced house blends engineered for every method.", slug: "blends" },
  { name: "Decaf", tag: "Nighttime Ritual", desc: "Full flavour, gentle finish — Swiss Water Processed.", slug: "decaf" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    client.get("/products?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <img
          src="https://images.unsplash.com/photo-1701735513192-bc7248978c51?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt="Coffee estate"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C3326]/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C3326]/85 via-[#1C3326]/30 to-transparent" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-20 md:pb-32 pt-32">
          <div className="max-w-3xl text-[#FDFBF7]">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#EBE3D5] mb-6 fade-up">
              Est. Karnataka · Grown in the mist
            </div>
            <h1 className="font-serif-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] font-light fade-up delay-100">
              Taste the<br /><em className="not-italic text-[#C85A32]">tradition</em>, sip by sip.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#EBE3D5] leading-relaxed fade-up delay-200">
              From our shaded slopes in Sakleshpur & Chikmagalur to your cup — small-batch,
              single-origin, and roasted the week it ships.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up delay-300">
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="inline-flex items-center gap-2 bg-[#C85A32] text-[#FDFBF7] hover:bg-[#A84A28] px-10 py-5 uppercase text-xs tracking-[0.3em] font-medium transition-colors"
              >
                Shop Coffee <ArrowRight size={14} />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 border border-[#FDFBF7]/60 text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#1C3326] px-10 py-5 uppercase text-xs tracking-[0.3em] font-medium transition-colors"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-[#1C3326] text-[#EBE3D5] py-4 overflow-hidden border-y border-[#2C1E16]">
        <div className="marquee-track whitespace-nowrap flex gap-16 text-xs uppercase tracking-[0.4em]">
          {Array(6).fill(null).map((_, i) => (
            <span key={i} className="flex items-center gap-16">
              Small-batch roasted <span className="text-[#C85A32]">◆</span>
              Estate-grown <span className="text-[#C85A32]">◆</span>
              Shade-cultivated <span className="text-[#C85A32]">◆</span>
              Free shipping above ₹999 <span className="text-[#C85A32]">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* Featured */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex justify-between items-end mb-16">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
              Our House Selection
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-none">
              Freshly Roasted<br />This Week.
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="home-view-all"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] link-underline"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
          {featured.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Coffee Varieties bento */}
      <section className="bg-[#F4EFE6] py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
                Coffee Varieties
              </div>
              <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-[0.95]">
                Taste the<br />distinction.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-[#4A3B32] text-lg leading-relaxed">
                Every bean tells the story of its terroir — the altitude, the shade trees, the
                monsoon. Explore four expressions of our estate, each with its own character.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {varieties.map((v, i) => (
              <Link
                to={`/shop?category=${v.slug}`}
                key={v.slug}
                data-testid={`variety-card-${v.slug}`}
                className={`group relative bg-[#FDFBF7] border border-[#EBE3D5] p-10 hover:border-[#1C3326] transition-colors ${
                  i % 4 === 0 ? "md:col-span-5" : i % 4 === 1 ? "md:col-span-7" : i % 4 === 2 ? "md:col-span-7" : "md:col-span-5"
                }`}
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
                  {v.tag}
                </div>
                <div className="font-serif-display text-5xl md:text-6xl leading-none font-light text-[#1C3326]">
                  {v.name}
                </div>
                <p className="mt-4 text-[#4A3B32] max-w-md">{v.desc}</p>
                <ArrowRight
                  size={18}
                  className="mt-6 text-[#1C3326] group-hover:translate-x-2 transition-transform"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Story split */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative aspect-[4/5] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1648483880685-356ea43870c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Estate"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
              Heritage & Quality
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-tight">
              A story of passion, innovation, and purpose.
            </h2>
            <p className="mt-6 text-[#4A3B32] leading-relaxed">
              Our estates span generations of planters. Every harvest is a conversation between
              soil, altitude, and the hands that pick — a partnership we've kept for decades.
            </p>
            <ul className="mt-8 space-y-3">
              {["Estate-direct sourcing", "Sustainable, shade-grown", "Small-batch roasted", "Traceable to the block"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm">
                  <span className="w-8 h-px bg-[#C85A32]" />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              data-testid="home-story-link"
              className="inline-block mt-10 border border-[#1C3326] text-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-10 py-4 uppercase text-xs tracking-[0.25em] font-medium transition-colors"
            >
              Read our story
            </Link>
          </div>
        </div>
      </section>

      {/* Services CTA */}
      <section className="bg-[#1C3326] text-[#FDFBF7] py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
            Experiences
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-none mb-16">
            Come, spend a day<br />on the estate.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Estate Tour", desc: "Walk the plantation with our planters. Understand the terroir, the shade trees, and the harvest." },
              { title: "Roasting Workshop", desc: "From green bean to first crack — hands-on with our master roasters." },
              { title: "Barista Training", desc: "Extraction, milk texture, latte art — everything you need to pour like a pro." },
            ].map((s) => (
              <div key={s.title} className="border-l border-[#EBE3D5]/30 pl-6" data-testid={`home-service-${s.title.toLowerCase().replace(" ", "-")}`}>
                <div className="font-serif-display text-3xl mb-3">{s.title}</div>
                <p className="text-[#EBE3D5] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <Link
            to="/services"
            data-testid="home-services-cta"
            className="inline-block mt-12 bg-[#C85A32] hover:bg-[#A84A28] px-10 py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors"
          >
            Book an experience
          </Link>
        </div>
      </section>

      {/* B2B Band */}
      <section className="bg-[#F4EFE6] py-20 md:py-24 border-y border-[#EBE3D5]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
              For Business
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-tight text-[#1C3326]">
              Running a café, hotel, or office?<br />
              <em className="not-italic text-[#C85A32]">Let's build your coffee program.</em>
            </h2>
            <p className="mt-6 text-[#4A3B32] leading-relaxed max-w-2xl">
              Beans · Equipment · Barista Training · Café Setup · Private Label · Corporate
              Beverage Solutions — one integrated partner, nationwide.
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex flex-col sm:flex-row md:flex-col gap-3">
            <Link
              to="/business"
              data-testid="home-b2b-cta"
              className="bg-[#1C3326] hover:bg-[#15261C] text-[#FDFBF7] text-center px-10 py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors"
            >
              Business Enquiry
            </Link>
            <Link
              to="/about"
              className="border border-[#1C3326] text-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] text-center px-10 py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors"
            >
              What we do
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
